import { useState, type ReactNode } from 'react'
import {
  endConsoleCloseWatch,
  setConsoleCap,
  setConsoleCloseWatch,
  type ConsoleBusiness,
} from '../../lib/api'
import { loadReason, money } from '../../lib/consoleStatus'
import { btn, btnGhost, card, h2Class, kicker } from '../soc/SocShell'

// BRIEF-F2: on a business's console page, staff change its daily analyst cap
// and put a person under close watch (or end one early). Every change goes
// through a plain confirmation step on the page (never a browser dialog)
// that shows the before and the after; only "Confirm" sends it. The server
// keeps the limits (US$20 a day, close watch at most 7 days, the person must
// be this business's) and says why when it refuses. Nothing here touches
// response or arming.

const MAX_CAP_USD = 20
const CLOSE_WATCH_MAX_DAYS = 7

const input =
  'min-h-[44px] rounded-md border border-[#cfc5b3] bg-white px-3 py-2 text-[15px] text-navy'

export interface PendingChange {
  what: string
  before: string
  after: string
  send: () => Promise<unknown>
}

/** The confirmation step: before and after, then Confirm or Cancel. */
export function ConfirmChange({
  change,
  onDone,
  onCancel,
}: {
  change: PendingChange
  onDone: () => void
  onCancel: () => void
}) {
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const confirm = async () => {
    setSending(true)
    setError(null)
    try {
      await change.send()
      onDone()
    } catch (err) {
      setError(loadReason(err))
      setSending(false)
    }
  }
  return (
    <div
      role="group"
      aria-label={`Confirm: ${change.what}`}
      data-testid="confirm-change"
      className="mt-3 rounded-[10px] border-2 border-brass bg-[#fbf6ea] px-4 py-3"
    >
      <div className="font-bold text-navy">{change.what}</div>
      <dl className="m-0 mt-2 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-[15px]">
        <dt className="text-[#6b5a3e]">Before</dt>
        <dd className="m-0" data-testid="confirm-before">
          {change.before}
        </dd>
        <dt className="text-[#6b5a3e]">After</dt>
        <dd className="m-0 font-bold" data-testid="confirm-after">
          {change.after}
        </dd>
      </dl>
      {error !== null && (
        <p role="alert" className="m-0 mt-2 text-[14px] font-bold text-danger">
          Not changed: {error}
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        <button type="button" className={btn} onClick={confirm} disabled={sending}>
          {sending ? 'Saving…' : 'Confirm'}
        </button>
        <button type="button" className={btnGhost} onClick={onCancel} disabled={sending}>
          Cancel
        </button>
      </div>
    </div>
  )
}

function capWords(usd: number, own: boolean): string {
  return `${money(usd)} a day (${own ? 'its own cap' : 'the standard cap'})`
}

/** The daily cap with "Change". */
export function CapControl({ b, onChanged }: { b: ConsoleBusiness; onChanged: () => void }) {
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingChange | null>(null)

  const check = () => {
    const usd = Number(value)
    if (!value.trim() || !Number.isFinite(usd) || usd <= 0 || usd > MAX_CAP_USD) {
      setProblem(`The cap must be above $0 and at most $${MAX_CAP_USD.toFixed(2)} a day.`)
      return
    }
    const rounded = Math.round(usd * 100) / 100
    setProblem(null)
    setPending({
      what: `Change ${b.name}'s daily analyst cap`,
      before: capWords(b.daily_cap_usd, b.cap_is_own),
      after: capWords(rounded, true),
      send: () => setConsoleCap(b.tenant_id, rounded),
    })
  }
  const reset = () => {
    setEditing(false)
    setPending(null)
    setValue('')
    setProblem(null)
  }

  return (
    <div data-testid="cap-control">
      <div className="flex flex-wrap items-center gap-3">
        <span className="text-[15px]">
          Daily analyst cap: <strong>{capWords(b.daily_cap_usd, b.cap_is_own)}</strong>
        </span>
        {!editing && (
          <button type="button" className={btnGhost} onClick={() => setEditing(true)}>
            Change
          </button>
        )}
      </div>
      {editing && pending === null && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="text-[14px] text-[#6b5a3e]" htmlFor={`cap-${b.tenant_id}`}>
            New cap (US$ a day, at most {MAX_CAP_USD})
          </label>
          <input
            id={`cap-${b.tenant_id}`}
            className={input}
            type="number"
            step="0.01"
            min="0.01"
            max={MAX_CAP_USD}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <button type="button" className={btn} onClick={check}>
            Next
          </button>
          <button type="button" className={btnGhost} onClick={reset}>
            Cancel
          </button>
          {problem !== null && (
            <p role="alert" className="m-0 w-full text-[14px] font-bold text-danger">
              {problem}
            </p>
          )}
        </div>
      )}
      {pending !== null && (
        <ConfirmChange
          change={pending}
          onCancel={reset}
          onDone={() => {
            reset()
            onChanged()
          }}
        />
      )}
    </div>
  )
}

function watchWords(until: string | undefined): string {
  return until
    ? `under close watch until the end of ${until}: read every few hours as well as daily`
    : 'not under close watch: read daily'
}

function PersonWatch({
  b,
  upn,
  onChanged,
}: {
  b: ConsoleBusiness
  upn: string
  onChanged: () => void
}) {
  const until = b.close_watch_until[upn.toLowerCase()]
  const [date, setDate] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [pending, setPending] = useState<PendingChange | null>(null)
  const id = `watch-${b.tenant_id}-${upn}`

  const watch = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      setProblem('Choose the last day of the close watch.')
      return
    }
    setProblem(null)
    setPending({
      what: `Watch ${upn} closely`,
      before: watchWords(until),
      after: watchWords(date),
      send: () => setConsoleCloseWatch(b.tenant_id, upn, date),
    })
  }
  const end = () =>
    setPending({
      what: `End the close watch on ${upn} early`,
      before: watchWords(until),
      after: watchWords(undefined),
      send: () => endConsoleCloseWatch(b.tenant_id, upn),
    })
  const reset = () => {
    setPending(null)
    setDate('')
    setProblem(null)
  }

  return (
    <li data-testid={`watch-${upn}`} className="border-b border-[#e6dfd2] py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="font-bold text-navy">{upn}</span>
        <span className="text-[14px] text-[#6b5a3e]">
          {until ? `Close watch until ${until}` : 'Daily read only'}
        </span>
      </div>
      {pending === null ? (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <label className="text-[14px] text-[#6b5a3e]" htmlFor={id}>
            Watch closely until
          </label>
          <input
            id={id}
            className={input}
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
          <button type="button" className={btn} onClick={watch}>
            {until ? 'Change the end' : 'Watch closely'}
          </button>
          {until && (
            <button type="button" className={btnGhost} onClick={end}>
              End
            </button>
          )}
          {problem !== null && (
            <p role="alert" className="m-0 w-full text-[14px] font-bold text-danger">
              {problem}
            </p>
          )}
        </div>
      ) : (
        <ConfirmChange
          change={pending}
          onCancel={reset}
          onDone={() => {
            reset()
            onChanged()
          }}
        />
      )}
    </li>
  )
}

/** Close watch per watched person, with "Watch closely until" and "End". */
export function CloseWatchControl({ b, onChanged }: { b: ConsoleBusiness; onChanged: () => void }) {
  const people = Array.from(new Set(b.feeds.filter((f) => f.watched).map((f) => f.upn)))
  return (
    <div data-testid="close-watch-control">
      <div className="text-[15px]">
        Close watch reads a person every few hours as well as daily, for at most{' '}
        {CLOSE_WATCH_MAX_DAYS} days.
      </div>
      {people.length === 0 ? (
        <p className="m-0 mt-2 text-[14px] text-[#6b5a3e]">Nobody of this business is watched.</p>
      ) : (
        <ul className="m-0 mt-1 list-none p-0">
          {people.map((upn) => (
            <PersonWatch key={upn} b={b} upn={upn} onChanged={onChanged} />
          ))}
        </ul>
      )}
    </div>
  )
}

function Block({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className={`${card} px-5 py-4`}>
      <h3 className="m-0 mb-2 font-serif text-[20px] font-normal">{title}</h3>
      {children}
    </div>
  )
}

/** The business's settings staff may change from the console. */
export function BusinessControls({ b, onChanged }: { b: ConsoleBusiness; onChanged: () => void }) {
  return (
    <section aria-label="Settings" className="flex flex-col gap-3.5">
      <div>
        <div className={kicker}>What OSELAB staff may change here</div>
        <h2 className={h2Class}>{b.name}</h2>
      </div>
      <Block title="Daily cap">
        <CapControl b={b} onChanged={onChanged} />
      </Block>
      <Block title="Close watch">
        <CloseWatchControl b={b} onChanged={onChanged} />
      </Block>
    </section>
  )
}
